import { MoreVert } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Theme,
} from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { AvailableLangs, LangSwitchOption } from "./../types/misc";
import { ContextMenuItem } from "./ContextMenu";
import { CtaButton } from "./EnigmaFlowChart/nodes";
import { useNavigate } from "react-router";

export type ChatItem = {
  id: string;
  heading?: {
    title?: string;
    description?: string;
    subheader?: ReactNode;
    avatar?: { url?: string; alt?: string; icon?: React.ReactNode };
  };
  html?: string;
  content?: string | ReactNode;
  media?: {
    type: "img" | "video" | "audio";
    url: string;
    alt: string;
  };
  cta?: string;
  more?: ContextMenuItem[];
  footer?: ReactNode;
  onCardClick?: () => void;
} & Record<string, unknown>;

export function ChatItemCard({
  item,
  theme,
  activeLang = "it-IT",
  onClick,
}: {
  item: ChatItem;
  theme?: Theme;
  onClick?: () => void;
  activeLang?: LangSwitchOption;
}) {
  const navigate = useNavigate();
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  
  if (!item) {
    return (
      <Card style={{ flex: 1, padding: "0 6px 4px" }}>
        <CardContent>
          <span>Elemento non disponibile</span>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card
      key={`${activeLang}-${item.id}`}
      style={{ flex: 1, padding: "0 6px 4px" }}
      onClick={onClick ?? item?.onCardClick}
    >
      <CardHeader
        avatar={
          item.heading?.avatar && (
            <Avatar
              alt={item.heading.avatar.alt}
              src={item.heading.avatar.url}
              slotProps={{
                img: {
                  crossOrigin: "anonymous",
                },
              }}
            >
              {item.heading?.avatar?.icon}
            </Avatar>
          )
        }
        action={
          <Box onClick={(e) => e.stopPropagation()}>
            <IconButton
              aria-label="settings"
              onClick={(e) => setMoreAnchorEl(e.currentTarget)}
            >
              <MoreVert />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={moreAnchorEl}
              open={Boolean(moreAnchorEl)}
              onClose={() => setMoreAnchorEl(null)}
            >
              {item.more?.map((option, i) => (
                <MenuItem
                  key={i}
                  onClick={() => {
                    option.onClick({ navigate });
                    setMoreAnchorEl(null);
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        }
        title={item.heading?.title}
        subheader={
          (item.heading?.description && (
            <span
              dangerouslySetInnerHTML={{
                __html: item.heading?.description || "",
              }}
            ></span>
          )) ||
          item.heading?.subheader
        }
      />
      {item.media && (
        <CardMedia>
          {item.media.type === "img" && (
            <img
              src={item.media.url}
              alt={item.media.alt}
              style={{ maxWidth: "100%" }}
              crossOrigin="anonymous"
            />
          )}
          {item.media.type === "video" && (
            <video
              src={item.media.url}
              controls
              style={{ maxWidth: "100%" }}
              crossOrigin="anonymous"
            />
          )}
          {item.media.type === "audio" && (
            <audio
              src={item.media.url}
              controls
              style={{ width: "100%" }}
              crossOrigin="anonymous"
            />
          )}
        </CardMedia>
      )}
      <CardContent>
        {item.html && (
          <div dangerouslySetInnerHTML={{ __html: item.html }}></div>
        )}
        {item.content}
      </CardContent>
      <CardActions sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch", gap: "8px" }}>
        {item.cta && <CtaButton label={item.cta} />}
        {item.footer}
      </CardActions>
    </Card>
  );
}

export function ChatItemTranslations({
  item,
  theme,
  activeLang,
}: {
  item: Record<AvailableLangs, ChatItem> & { id: string };
  theme?: Theme;
  activeLang?: AvailableLangs;
}) {
  const getTranslation = (lang: AvailableLangs) => {
    const translation = item[lang];
    if (!translation) {
      return (
        <Card
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span>Traduzione non disponibile</span>
        </Card>
      );
    }

    return (
      <ChatItemCard
        key={`${lang}-${item.id}`}
        item={translation}
        theme={theme}
        activeLang={lang}
      />
    );
  };

  const translations = useMemo(() => {
    return {
      "it-IT": getTranslation("it-IT"),
      "en-US": getTranslation("en-US"),
    };
  }, [item, theme]);

  if (activeLang) {
    return translations[activeLang];
  }

  return (
    <Box>
      <Stack key={item.id} spacing={2} direction="row" flex={1}>
        {translations["it-IT"]}
        {translations["en-US"]}
      </Stack>
    </Box>
  );
}
